import { useMemo } from "react";
import { styled } from "@pigment-css/react";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import {
  CalendarGrid,
  CalendarHeader,
  todayUtc,
  useCalendarMonth,
} from "@acto/taskpane/field-sales-2/components/primitives/Calendar";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";

// Transparent layout — the consumer (e.g. the create-todo sheet's due-date card) supplies the
// surrounding glass container, so the calendar can sit inline within it.
const CalendarLayout = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[24],
  width: "100%",
}));

interface DatePickerProps {
  /** Currently selected day (UTC midnight), or null for no selection. */
  value: Date | null;
  /** Notified when a selectable day is chosen (UTC midnight). */
  onChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  /** When false, weekends are hidden (Mo–Fr business-day grid). Defaults to true (full week). */
  includeWeekends?: boolean;
}

/**
 * Single-date month picker built on the shared Calendar primitive (`useCalendarMonth` +
 * `CalendarHeader`/`CalendarGrid`). Dates are UTC midnight to match the backend's `YYYY-MM-DD`
 * LocalDate boundary. Includes a month-jump dropdown; weekends can be hidden via `includeWeekends`.
 */
export function DatePicker({ value, onChange, minDate, maxDate, includeWeekends = true }: DatePickerProps) {
  const t = useTranslations("fieldSalesTwo.todoCreate");
  const { locale } = useLocale();
  const calendar = useCalendarMonth({
    selectedDate: value,
    onSelectDate: onChange,
    minDate,
    maxDate,
    includeWeekends,
  });

  const visibleMonth = calendar.visibleMonth ?? todayUtc();
  const year = visibleMonth.getUTCFullYear();
  const currentMonthIndex = visibleMonth.getUTCMonth();
  const monthFormatter = useMemo(() => new Intl.DateTimeFormat(locale, { month: "long", timeZone: "UTC" }), [locale]);
  const months = Array.from({ length: 12 }, (_, index) => ({
    index,
    label: monthFormatter.format(new Date(Date.UTC(year, index, 1))),
  }));

  const monthDropdown = calendar.goToMonth
    ? {
        year: String(year),
        monthLabel: months[currentMonthIndex].label,
        months,
        currentIndex: currentMonthIndex,
        onSelect: calendar.goToMonth,
        triggerLabel: t("calendar.selectMonth"),
      }
    : undefined;

  return (
    <CalendarLayout>
      <CalendarHeader
        title={calendar.title}
        previousLabel={t("calendar.previousMonth")}
        nextLabel={t("calendar.nextMonth")}
        onPrevious={calendar.goToPrevious}
        onNext={calendar.goToNext}
        monthDropdown={monthDropdown}
      />
      <Divider />
      <CalendarGrid weekdayLabels={calendar.weekdayLabels} weeks={calendar.weeks} onSelectDay={calendar.selectDay} />
    </CalendarLayout>
  );
}
